/**
 * Single catalog for public open-source canvas projects (gallery, SEO, display names).
 * Slugs must also exist in `REACT_APP_PUBLIC_PROJECTS` with backend project UUIDs.
 *
 * @see src/constants/README.md — how to add a new public project
 */
import catalog from "./publicOpenSourceProjects.json";
import { getPublicProjects } from "../common/publicUtils";
import axiosLogo from "../images/openSourceRepoLogos/axios.svg";
import pyramidLogo from "../images/openSourceRepoLogos/pyramid.svg";
import flaskLogo from "../images/openSourceRepoLogos/flask.svg";
import zodLogo from "../images/openSourceRepoLogos/zod.svg";
import honoLogo from "../images/openSourceRepoLogos/hono.svg";
import nanoclawLogo from "../images/openSourceRepoLogos/nanoclaw.svg";
import nvidiaLogo from "../images/openSourceRepoLogos/nvidia.svg";
import pydanticLogo from "../images/openSourceRepoLogos/pydantic.svg";
import openclawLogo from "../images/openSourceRepoLogos/openclaw.svg";
import openhandsLogo from "../images/openSourceRepoLogos/openhands.svg";
import instroLogo from "../images/openSourceRepoLogos/nominal.svg";

export type PublicOpenSourceComponent = {
  name: string;
  description: string;
};

/**
 * Long-form content for `/open-source-projects/:slug`. Optional — only projects
 * with a `page` block get a content route, so we never publish templated
 * near-duplicate pages for projects we have nothing to say about.
 */
export type PublicOpenSourceProjectPage = {
  tagline: string;
  about: string[];
  components: PublicOpenSourceComponent[];
};

export type PublicOpenSourceProject = {
  slug: string;
  name: string;
  description: string;
  github: string;
  logo: string;
  page?: PublicOpenSourceProjectPage;
};

export type PublicOpenSourceGalleryProject = PublicOpenSourceProject & {
  href: string;
  logoSrc: string;
};

/** Maps `logo` filenames in JSON to bundled asset URLs. Add an entry when adding a logo. */
const LOGO_BY_FILE: Record<string, string> = {
  "axios.svg": axiosLogo,
  "pyramid.svg": pyramidLogo,
  "flask.svg": flaskLogo,
  "zod.svg": zodLogo,
  "hono.svg": honoLogo,
  "nanoclaw.svg": nanoclawLogo,
  "nvidia.svg": nvidiaLogo,
  "pydantic.svg": pydanticLogo,
  "openclaw.svg": openclawLogo,
  "openhands.svg": openhandsLogo,
  "nominal.svg": instroLogo,
};

function resolveLogoSrc(logoFile: string): string {
  const src = LOGO_BY_FILE[logoFile];
  if (!src && process.env.NODE_ENV === "development") {
    console.warn(
      `[publicOpenSourceProjects] Missing logo mapping for: ${logoFile}`,
    );
  }
  return src ?? "";
}

const projectsBySlug = new Map<string, PublicOpenSourceProject>(
  (catalog.projects as PublicOpenSourceProject[]).map((project) => [
    project.slug,
    project,
  ]),
);

/** All projects in gallery display order. */
export function getPublicOpenSourceCatalog(): PublicOpenSourceProject[] {
  return catalog.projects as PublicOpenSourceProject[];
}

export function getPublicOpenSourceProject(
  slug: string,
): PublicOpenSourceProject | null {
  return projectsBySlug.get(slug) ?? null;
}

export function getPublicOpenSourceDisplayName(slug: string): string {
  const project = getPublicOpenSourceProject(slug);
  if (project) {
    return project.name;
  }
  const normalized = slug.toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

/** Gallery cards for `/open-source-projects`. */
export function getPublicOpenSourceGalleryProjects(): PublicOpenSourceGalleryProject[] {
  return getPublicOpenSourceCatalog().map((project) => ({
    ...project,
    href: `/canvas/${project.slug}`,
    logoSrc: resolveLogoSrc(project.logo),
  }));
}

/** Slugs configured in env (`REACT_APP_PUBLIC_PROJECTS`) that exist in the catalog. */
export function getActivePublicOpenSourceSlugs(): string[] {
  const envSlugs = Object.keys(getPublicProjects());
  return envSlugs.filter((slug) => projectsBySlug.has(slug)).sort();
}

export function getPublicOpenSourceLogoSrc(slug: string): string {
  const project = getPublicOpenSourceProject(slug);
  return project ? resolveLogoSrc(project.logo) : "";
}

/**
 * Slugs that have both long-form `page` content and an active env mapping, so
 * `/open-source-projects/:slug` can render a real content page for them.
 */
export function getContentPageSlugs(): string[] {
  return getActivePublicOpenSourceSlugs().filter(
    (slug) => projectsBySlug.get(slug)?.page != null,
  );
}
